import React, { useState, useEffect } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useBluetooth } from '../../contexts/BluetoothContext';
import { useSensorData } from '../../contexts/SensorDataContext';
import { 
  BluetoothDisabled, 
  Bluetooth, 
  BluetoothSearching, 
  BluetoothConnected 
} from '@mui/icons-material';
import GlobalBluetoothWrapper from '../../utils/GlobalBluetoothWrapper';
import './GlobalBluetoothStatus.css';

// Safe hook to use SensorDataContext without throwing errors
const useSensorDataSafe = () => {
  try {
    return useSensorData();
  } catch (error) {
    // SensorDataContext not available in this context
    return null;
  }
};

interface GlobalBluetoothStatusProps extends WithTranslation {
  onConnectClick?: () => void;
  onCalibrateClick?: () => void;
  onDisconnectClick?: () => void;
}

const GlobalBluetoothStatus: React.FC<GlobalBluetoothStatusProps> = ({
  t,
  onConnectClick,
  onCalibrateClick,
  onDisconnectClick,
}) => {
  const {
    isConnected,
    isConnecting,
    isCalibrated,
    isCalibrating,
    isDataStreamActive,
    connect,
    disconnect,
    startCalibration,
  } = useBluetooth();

  // Get sensor data context (optional - may not be available in all contexts)
  // We'll use a custom hook that safely handles the case where context is not available
  const sensorDataContext = useSensorDataSafe();

  const [bluetoothAvailable, setBluetoothAvailable] = useState<boolean | null>(null);

  // Check Bluetooth availability
  useEffect(() => {
    const checkBluetoothAvailability = async () => {
      if (!navigator.bluetooth) {
        setBluetoothAvailable(false);
        return;
      }

      try {
        const available = await navigator.bluetooth.getAvailability();
        setBluetoothAvailable(available);
      } catch (error) {
        setBluetoothAvailable(true);
      }
    };

    checkBluetoothAvailability();
  }, []);

  // Use the new callback registration system to keep Redux in sync if needed,
  // but primarily we should rely on useBluetooth hook's internal subscription.
  // This component doesn't actually need to set its own callbacks anymore
  // because useBluetooth (via BluetoothContext) already does it correctly.
  
  // Periodic connection check to detect disconnections
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      const wrapper = GlobalBluetoothWrapper.getInstance();
      wrapper.checkConnectionStatus();
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [isConnected]);

  const handleClick = () => {
    if (isConnecting || isCalibrating || bluetoothAvailable === false) return;
    
    if (!isConnected) {
      if (onConnectClick) {
        onConnectClick();
      } else {
        connect();
      }
    } else if (!isCalibrated) {
      if (onCalibrateClick) {
        onCalibrateClick();
      } else {
        startCalibration();
      }
    } else {
      if (onDisconnectClick) {
        onDisconnectClick();
      } else {
        disconnect();
      }
    }
  };

  const getIcon = () => {
    if (bluetoothAvailable === false) return <BluetoothDisabled />; // Bluetooth not available
    if (isConnecting || isCalibrating) return <BluetoothSearching />; // Searching/connecting
    if (isDataStreamActive || (sensorDataContext && sensorDataContext.isDataStreamActive)) return <BluetoothConnected />; // Data stream is active (connected and ready)
    if (isConnected && !isDataStreamActive) return <Bluetooth />; // Connected but data stream not active
    return <Bluetooth />; // Not connected
  };

  const getColor = () => {
    if (bluetoothAvailable === false) return '#9e9e9e'; // Grey for not available
    if (isConnecting || isCalibrating) return '#ff9800'; // Orange for searching
    if (isDataStreamActive || (sensorDataContext && sensorDataContext.isDataStreamActive)) return '#ffffff'; // White for data stream active (connected and ready)
    if (isConnected && !isDataStreamActive) return '#ffffff'; // White for connected but data stream not active
    return '#9e9e9e'; // Grey for not connected
  };

  const getStatusText = () => {
    if (bluetoothAvailable === false) return 'Bluetooth not available';
    if (isConnecting) return 'Connecting to device...';
    if (isCalibrating) return 'Calibrating device...';
    if (isDataStreamActive || (sensorDataContext && sensorDataContext.isDataStreamActive)) {
      const force = sensorDataContext ? sensorDataContext.processedForce : 0;
      const forceText = force === 'MAX' ? 'MAX' : force === 'Out' ? 'Out' : `${force.toFixed(1)}kg`;
      return `Device Connected & Active (${forceText})`;
    }
    if (isConnected && !isDataStreamActive) return 'Device Connected - Ready for calibration';
    return 'Device Not Connected - Click to connect';
  };

  const isClickable = !isConnecting && !isCalibrating && bluetoothAvailable !== false;

  return (
    <div className="bluetooth-overlay" title={getStatusText()}>
      <div
        className={`bluetooth-icon-container ${(isConnecting || isCalibrating) ? 'bluetooth-spinning' : ''}`}
        onClick={handleClick}
        style={{ 
          cursor: isClickable ? 'pointer' : 'not-allowed',
          opacity: isClickable ? 1 : 0.5,
          color: getColor()
        }}
      >
        {getIcon()}
      </div>
    </div>
  );
};

export default withTranslation('workout')(GlobalBluetoothStatus);
